import React, { useState } from "react";
import { Card, Typography, TextField, Button, Avatar, Alert, Box, Chip } from "@mui/material";
import { FaUserCircle, FaEdit } from "react-icons/fa";
import COLORS from "../../../../../constants/colors";
import baseURL from "../../config";
import UpdateProfileDialog from "./UpdateProfileDialog";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { toast } from "react-toastify";

const ProfileInformation = ({ profile, onProfileUpdated }) => {
  const [openDialog, setOpenDialog] = useState(false);

  const missingFields = [];
  if (!profile.phone) missingFields.push("Phone Number");
  if (!profile.profileImage) missingFields.push("Profile Picture");
  if (!profile.physical_address) missingFields.push("Physical Address");
  if (!profile.idFront) missingFields.push("Front ID picture");
  if (!profile.idBack) missingFields.push("Back ID picture");

  const handleSave = async (updatedData) => {
    try {
      const response = await fetch(`${baseURL}/user/profile/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) throw new Error("Failed to update profile");
      
      setOpenDialog(false);
      toast.success("Profile updated successfully.");
      onProfileUpdated(); // Call the parent's callback to trigger a data re-fetch
      
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile.");
    }
  };

  return (
    <Card sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, textAlign: "left", mb: 4, mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "1.05rem", letterSpacing: "-0.3px", mb: 2, display: "flex", alignItems: "center" }}>
        <FaUserCircle style={{ marginRight: 8, color: "#2D4A3D" }} />
        User Profile
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          mb: 3,
          gap: { xs: 1.5, sm: 0 },
        }}
      >
        <Avatar
          src={profile.profileImage || ""}
          sx={{
            width: 70,
            height: 70,
            bgcolor: profile.profile_image ? "transparent" : COLORS.primary,
            fontSize: "1.5rem",
            mr: { xs: 0, sm: 2 },
          }}
        >
          {!profile.profileImage && profile.name?.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: { xs: 1, sm: 0 } }}>
          <Typography variant="body1" sx={{ fontWeight: "bold", color: "#444", fontSize: { xs: "0.9rem", sm: "1rem" } }}>
            Account Status:
          </Typography>
          <Chip
            label={profile.isVerified === "yes" ? "Approved" : "Pending Approval"}
            icon={profile.isVerified === "yes" ? <CheckCircleIcon sx={{ color: "#2E7D32" }} /> : <HourglassEmptyIcon sx={{ color: "#C07600" }} />}
            sx={{
              bgcolor: profile.isVerified === "yes" ? "#E3FCEF" : "#FFF5E1",
              color: profile.isVerified === "yes" ? "#2E7D32" : "#C07600",
              fontWeight: "bold",
              fontSize: "0.7rem",
              borderRadius: "5px",
              px: 1.5,
              py: 0.5,
            }}
          />
        </Box>
      </Box>
      {missingFields.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3, fontSize: "0.85rem", borderRadius: 2 }}>
          <strong>Your profile is almost complete!</strong> You're missing the following details:{" "}
          <strong>{missingFields.join(", ")}</strong>. Please update these fields to complete your profile and start making donations on the Yamba platform!
        </Alert>
      )}
      <TextField
        label="Full Name"
        variant="outlined"
        fullWidth
        value={profile.name || ''}
        InputProps={{ readOnly: true }}
        sx={inputStyles}
      />
      <TextField
        label="Email Address"
        variant="outlined"
        fullWidth
        value={profile.email || ''}
        InputProps={{ readOnly: true }}
        sx={inputStyles}
      />
      <TextField
        label="Phone Number"
        variant="outlined"
        fullWidth
        value={profile.phone || "Not Available"}
        InputProps={{ readOnly: true }}
        sx={{
          ...inputStyles,
          border: missingFields.includes("Phone Number") ? `1px solid ${COLORS.error}` : "none",
        }}
      />
      <Button variant="contained" onClick={() => setOpenDialog(true)} sx={buttonStyles}>
        <FaEdit style={{ marginRight: 8 }} />
        Update Information
      </Button>
      <UpdateProfileDialog open={openDialog} onClose={() => setOpenDialog(false)} profile={profile} onSave={handleSave} />
    </Card>
  );
};

const inputStyles = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
};

const buttonStyles = {
  backgroundColor: COLORS.primary,
  textTransform: "none",
  px: 3,
  py: 1,
  borderRadius: "8px",
  "&:hover": { backgroundColor: COLORS.primary, opacity: 0.9 },
  display: "flex",
  alignItems: "center",
};

export default ProfileInformation;